import React, { useState } from 'react';

import moment, { Moment } from 'moment';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';

import { UiButton } from '../../../ui/ui-button';
import { UiDatePicker } from '../../../ui/ui-date-picker';
import { UiDialog } from '../../../ui/ui-dialog';

import { formatDate } from '../../../util/formatters';
import { TableHeaderCell } from '../../../widgets/table-components/table-header-cell';

type InputProps = {
  row: EmployeeDirectoryImportDataComparison;
  onClose: (resolution?: EmployeeDirectoryImportSimpleContact) => void;
};

type GenericRowProps = {
  label: string;
  field: keyof EmployeeDirectoryImportSimpleContact;
  existingDataValue: string | null;

  value: string | null;
  useTeamEngineValue: (field: keyof EmployeeDirectoryImportSimpleContact) => () => void;
};

type RowProps = GenericRowProps & {
  updateField: (
    field: keyof EmployeeDirectoryImportSimpleContact,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const ContactComparisonRow = ({
  label,
  field,
  existingDataValue,
  value,
  updateField,
  useTeamEngineValue,
}: RowProps) => {
  const hasDiscrepancy = existingDataValue && value && value !== existingDataValue;
  const onClickHandler = useTeamEngineValue(field);
  if (!hasDiscrepancy) {
    return (
      <TableRow>
        <TableCell>
          <span className="typo-bold">{label}</span>
        </TableCell>
        <TableCell>{value}</TableCell>
        <TableCell>{existingDataValue}</TableCell>
      </TableRow>
    );
  }
  return (
    <TableRow className="u-background-messageWarning">
      <TableCell>
        <span className="typo-bold">{label}</span>
      </TableCell>
      <TableCell>
        <TextField className="u-width-100" value={value} onChange={updateField(field)} />
      </TableCell>
      <TableCell>
        {existingDataValue}{' '}
        <UiButton size="xxs" variant="tertiary" onClick={onClickHandler}>
          Use
        </UiButton>
      </TableCell>
    </TableRow>
  );
};

type DateRowProps = GenericRowProps & {
  updateField: (field: keyof EmployeeDirectoryImportSimpleContact) => (e: Moment | null) => void;
};

const ContactComparisonDateRow = ({
  label,
  field,
  existingDataValue,
  value,
  updateField,
  useTeamEngineValue,
}: DateRowProps) => {
  // We store the birthdate on person without the time component, so we need to add it to compare to the import column, which
  // does have it
  let existingDataValueWithTime = existingDataValue;
  const regex = /.*T\d\d:\d\d:\d\d\.000Z/gm;
  if (existingDataValue && regex.exec(existingDataValue) === null) {
    existingDataValueWithTime = `${existingDataValue}T00:00:00.000Z`;
  }
  const existingDataMoment = moment.utc(existingDataValueWithTime);
  const valueMoment = moment.utc(value);
  const hasDiscrepancy =
    existingDataValue && value && !existingDataMoment.isSame(valueMoment, 'day');
  const onClickHandler = useTeamEngineValue(field);

  if (!hasDiscrepancy) {
    return (
      <TableRow>
        <TableCell>
          <span className="typo-bold">{label}</span>
        </TableCell>
        <TableCell>{formatDate(value)}</TableCell>
        <TableCell>{formatDate(existingDataValue)}</TableCell>
      </TableRow>
    );
  }
  return (
    <TableRow className="u-background-messageWarning">
      <TableCell>
        <span className="typo-bold">{label}</span>
      </TableCell>
      <TableCell>
        <UiDatePicker
          className="u-width-100"
          defaultDate={valueMoment}
          onChange={updateField(field)}
        />
      </TableCell>
      <TableCell>
        {formatDate(existingDataValue)}{' '}
        <UiButton size="xxs" variant="tertiary" onClick={onClickHandler}>
          Use
        </UiButton>
      </TableCell>
    </TableRow>
  );
};

export const SimpleContactComparisonDialog = ({ row, onClose }: InputProps) => {
  const { existingData, importedData } = row;

  const [resolutionData, setResolutionData] =
    useState<EmployeeDirectoryImportSimpleContact>(importedData);

  const onCancel = () => onClose();
  const onResolve = () => onClose(resolutionData);

  const updateField =
    (field: keyof EmployeeDirectoryImportSimpleContact) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setResolutionData({
        ...resolutionData,
        [field]: e.target.value,
      });
    };
  const updateDateField =
    (field: keyof EmployeeDirectoryImportSimpleContact) => (d: Moment | null) => {
      setResolutionData({
        ...resolutionData,
        [field]: d,
      });
    };
  const useTeamEngineValue = (field: keyof EmployeeDirectoryImportSimpleContact) => () => {
    setResolutionData({
      ...resolutionData,
      [field]: existingData[field],
    });
  };

  return (
    <UiDialog onClose={onCancel} title="Resolve Data Discrepancy" size="md">
      <p className="typo-subtitle">
        There is data in this list that conflicts with the data in the system. Please fill in the
        highlighted data with the correct value.
      </p>
      <UiDialog.Content>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableHeaderCell />
              <TableHeaderCell>Imported Value</TableHeaderCell>
              <TableHeaderCell>Team Engine Value</TableHeaderCell>
              <TableHeaderCell />
            </TableRow>
          </TableHead>
          <TableBody>
            <ContactComparisonRow
              label="First Name"
              field="firstName"
              value={resolutionData.firstName}
              existingDataValue={existingData.firstName}
              updateField={updateField}
              useTeamEngineValue={useTeamEngineValue}
            />
            <ContactComparisonRow
              label="Nickname"
              field="nickname"
              value={resolutionData.nickname}
              existingDataValue={existingData.nickname}
              updateField={updateField}
              useTeamEngineValue={useTeamEngineValue}
            />
            <ContactComparisonRow
              label="Last Name"
              field="lastName"
              value={resolutionData.lastName}
              existingDataValue={existingData.lastName}
              updateField={updateField}
              useTeamEngineValue={useTeamEngineValue}
            />
            <ContactComparisonRow
              label="Email"
              field="email"
              value={resolutionData.email}
              existingDataValue={existingData.email}
              updateField={updateField}
              useTeamEngineValue={useTeamEngineValue}
            />
            <ContactComparisonRow
              label="Phone Number"
              field="phoneNumber"
              value={resolutionData.phoneNumber}
              existingDataValue={existingData.phoneNumber}
              updateField={updateField}
              useTeamEngineValue={useTeamEngineValue}
            />
            <ContactComparisonDateRow
              label="Birthdate"
              field="birthdate"
              value={resolutionData.birthdate}
              existingDataValue={existingData.birthdate}
              updateField={updateDateField}
              useTeamEngineValue={useTeamEngineValue}
            />
            <ContactComparisonDateRow
              label="Start Date"
              field="startDate"
              value={resolutionData.startDate}
              existingDataValue={existingData.startDate}
              updateField={updateDateField}
              useTeamEngineValue={useTeamEngineValue}
            />
            <ContactComparisonRow
              label="Employee Identifier"
              field="externalId"
              value={resolutionData.externalId}
              existingDataValue={existingData.externalId}
              updateField={updateField}
              useTeamEngineValue={useTeamEngineValue}
            />
          </TableBody>
        </Table>
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton variant="dismiss" onClick={onCancel}>
          Cancel
        </UiButton>
        <UiButton variant="primary" onClick={onResolve}>
          Resolve Discrepancy
        </UiButton>
      </UiDialog.Actions>
    </UiDialog>
  );
};
